import { useCallback, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import { getApiErrorMessage } from "../api";

interface RefreshOptions {
  /** 最小実行間隔（ミリ秒）。デフォルト: 3秒 */
  minInterval?: number;
  /** デバウンス時間（ミリ秒）。デフォルト: 300ms */
  debounceDelay?: number;
  /** エラー時にトースト表示するか */
  showErrorToast?: boolean;
  /** 成功時にトースト表示するか */
  showSuccessToast?: boolean;
}

/**
 * 複数のデータ取得関数を効率的に実行する共通フック
 * - デバウンス処理で連続実行を防止
 * - スロットル処理で最小間隔を確保
 * - 進行中フラグで重複リクエスト防止
 * - 複数の非同期処理を並行実行
 */
export const useRefresh = (options: RefreshOptions = {}) => {
  const {
    minInterval = 3000,
    debounceDelay = 300,
    showErrorToast = true,
    showSuccessToast = false,
  } = options;

  const { showToast } = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isRefreshingRef = useRef(false);
  const lastRefreshTimeRef = useRef<number>(0);
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const runFetchFns = useCallback(
    async (fetchFns: Array<() => Promise<void>>) => {
      const results = await Promise.allSettled(fetchFns.map((fn) => fn()));
      const rejected = results.filter(
        (result): result is PromiseRejectedResult => result.status === "rejected",
      );

      if (rejected.length > 0) {
        const firstErrorMessage = getApiErrorMessage(rejected[0].reason);
        if (showErrorToast) {
          showToast(firstErrorMessage, "error");
        }
        throw rejected[0].reason;
      }
    },
    [showErrorToast, showToast],
  );

  /**
   * 複数の非同期関数を並行実行
   * - スロットル: 最後のリフレッシュから minInterval ミリ秒以内なら実行スキップ
   * - デバウンス: 連続呼び出しは debounceDelay ミリ秒待機
   * - 重複防止: 進行中は新規実行をスキップ
   */
  const refresh = useCallback(
    async (...fetchFns: Array<() => Promise<void>>) => {
      // 進行中なら実行スキップ
      if (isRefreshingRef.current) {
        return;
      }

      // デバウンスタイマーをクリア
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // デバウンス処理：指定時間後に実行
      debounceTimerRef.current = setTimeout(async () => {
        const now = Date.now();
        const timeSinceLastRefresh = now - lastRefreshTimeRef.current;

        // スロットル：最小間隔に達していない場合はスキップ
        if (timeSinceLastRefresh < minInterval) {
          return;
        }

        // 進行中フラグを立てる
        isRefreshingRef.current = true;
        setIsRefreshing(true);

        try {
          // 全ての fetch 関数を並行実行
          await runFetchFns(fetchFns);

          lastRefreshTimeRef.current = Date.now();

          if (showSuccessToast) {
            showToast("データを更新しました", "success");
          }
        } catch (error) {
          void error;
        } finally {
          isRefreshingRef.current = false;
          setIsRefreshing(false);
        }
      }, debounceDelay);
    },
    [debounceDelay, minInterval, showErrorToast, showSuccessToast, showToast],
  );

  /**
   * 即座に実行（デバウンス・スロットル無視）
   * UIブロックを避けるため、非同期で実行
   */
  const refreshImmediate = useCallback(
    async (...fetchFns: Array<() => Promise<void>>) => {
      if (isRefreshingRef.current) {
        return;
      }

      isRefreshingRef.current = true;
      setIsRefreshing(true);

      try {
        await runFetchFns(fetchFns);

        lastRefreshTimeRef.current = Date.now();

        if (showSuccessToast) {
          showToast("データを更新しました", "success");
        }
      } catch (error) {
        void error;
      } finally {
        isRefreshingRef.current = false;
        setIsRefreshing(false);
      }
    },
    [runFetchFns, showSuccessToast, showToast],
  );

  return {
    isRefreshing,
    refresh,
    refreshImmediate,
  };
};
